import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from 'react-infinite-scroll-component';

import {
  BackButton,
  Button,
  ButtonsWrapper,
  Header,
  NotificationListWrapper,
  Placeholder,
  PlaceholderText,
  ScrollWrapper,
  StickyWrapper,
  ArchiveDropdownWrapper,
  Tab,
  TabMenuButton,
  TabsWrapper,
  TabText,
  Title,
} from "./styles";
import NotificationListItem from "./NotificationListItem";
import { NotificationTabs, useNotificationContext } from "../../contexts/NotificationContext";
import { useModal } from "../../../core";
import { useTranslation } from "react-i18next";
import { Circle, Count } from "./NotificationBellIcon";
import { DEFAULT_DROPDOWN_HEIGHT, MAX_ITEMS_COUNT_FOR_SCROLL, SCROLL_ITEMS_NAME, STICKY_BLOCK_SIZE } from "./const/const";
import { Dropdown, DropdownListItem, useAdvancedLockBodyScroll, useBreakpoint, useIsMobile } from "@brg/ui";
import { AppTester } from "supporting";
import { MoreButtonTarget } from "shared/components/more-button";
import {
  countUnreadInCategory,
  filterNotificationsByCategory,
  NotificationBellCategory,
} from "./helpers/notificationCategory";

const NotificationList: FC = () => {
  const {
    notifications,
    unreadCount,
    currentTab,
    setCurrentTab,
    archived,
    getMore,
    paging,
    setScroll,
    open,
    updateNotificationStatus,
    archiveRead,
    setPopUpHeight,
  } = useNotificationContext();
  const [lockBody, unlockBody] = useAdvancedLockBodyScroll();
  const modal = useModal();
  const { t, i18n: { language } } = useTranslation();
  const scrollRef = useRef(null);
  const scrollArchiveRef = useRef(null);
  const isMobile = useIsMobile();
  const isCGRAVE = AppTester.isCGRAVE();
  const isHSEDesign = AppTester.isHSEDesign();
  const isGreenAward = AppTester.isGreenAward();
  const isGameDeziiign = AppTester.isGameDeziiign();
  const isJhonata = AppTester.isJhonataNet()
  const isTablet = useBreakpoint(1199);
  const [scrollContainer, setScrollContainer] = useState<any>(null);

  const inboxNotifications = useMemo(() => {
    if (!notifications?.length) {
      return [];
    }
    switch (currentTab) {
      case NotificationTabs.activity:
        return filterNotificationsByCategory(notifications, NotificationBellCategory.activity);
      case NotificationTabs.courses:
        return filterNotificationsByCategory(notifications, NotificationBellCategory.courses);
      case NotificationTabs.services:
        return filterNotificationsByCategory(notifications, NotificationBellCategory.services);
      case NotificationTabs.all:
      default:
        return notifications;
    }
  }, [notifications, currentTab]);

  const categoryUnreadCounts = useMemo(() => ({
    [NotificationBellCategory.activity]: countUnreadInCategory(notifications, NotificationBellCategory.activity),
    [NotificationBellCategory.courses]: countUnreadInCategory(notifications, NotificationBellCategory.courses),
    [NotificationBellCategory.services]: countUnreadInCategory(notifications, NotificationBellCategory.services),
  }), [notifications]);

  const isInboxTab = currentTab !== NotificationTabs.archived;

  const notificationsList = useMemo(() => {
    let rowNots: any = [];

    if (!inboxNotifications?.length) {
      return rowNots;
    }

    inboxNotifications?.forEach((n, idx, arr) => {
      const isLike = n?.actions?.[0]?.type === 'like';

      const key = isLike ? n?.actions?.[0]?.likeInfo?.entityInfo?.entityId : n?.notificationId;
      const name = isLike ? n?.actions?.[0]?.likeInfo?.initiator?.accountId : '';
      const direction = isLike ? n?.actions?.[0]?.likeInfo?.isIncrement ?? true : true;

      const current = isLike
        ? arr[idx]?.actions?.[0]?.likeInfo?.entityInfo?.entityId
        : arr[idx]?.notificationId;      
      const prev = isLike
        ? arr[idx - 1]?.actions?.[0]?.likeInfo?.entityInfo?.entityId
        : arr[idx - 1]?.notificationId;      
      const second = isLike
        ? arr[idx + 1]?.actions?.[0]?.likeInfo?.entityInfo?.entityId
        : arr[idx + 1]?.notificationId;

      const currentDirection = isLike
        ? arr[idx]?.actions?.[0]?.likeInfo?.isIncrement ?? true
        : true
      const prevDirection = isLike
        ? arr[idx - 1]?.actions?.[0]?.likeInfo?.isIncrement ?? true
        : true
      const secondDirection = isLike
        ? arr[idx + 1]?.actions?.[0]?.likeInfo?.isIncrement ?? true
        : true

      if ((current === prev && currentDirection === prevDirection) ||
        (current === second && currentDirection === secondDirection)) {

        const mainRow = rowNots?.find((i: any) => i?.rowId === key && !!i?.mainRow )
        if (!!mainRow) {
          const newValues = rowNots?.map((i: any) => {
            const doubleLike = i?.actions?.[0]?.likeInfo?.initiator?.accountId;
            const doubleLikeDirection = i?.actions?.[0]?.likeInfo?.isIncrement ?? true;
            if (i?.rowId === key && (doubleLike !== name || doubleLikeDirection !== direction)) {
              return {
                ...i,
                rowCount: i?.rowCount + 1
              }
            } return i
          })
          rowNots = newValues;
        }
        else {
          rowNots.push({
            ...n,
            rowId: key,
            mainRow: true,
            rowCount: 0
          })
        }
      } else {
        rowNots.push(n)
      };
    })

    return rowNots?.map((notification: any) => (
      <NotificationListItem key={notification.notificationId} item={notification}/>
    ))
  }, [inboxNotifications, language])

  const archivedList = useMemo(() => {
    return archived?.map((notification) => (
      <NotificationListItem key={notification.notificationId} item={notification}/>
    ))
  }, [archived, language])

  useEffect(() => {
    const scrollContainer = document.querySelector('#notification-dropdown:not(.dropdown-element-popup):last-child');
    setScrollContainer(scrollContainer)
  }, [])

  useEffect(() => {
    if ((scrollRef.current || scrollArchiveRef.current ) && open) {
      const scrollEl = document.querySelector(currentTab === NotificationTabs.archived  ? '.notification-inf-scroll_achirved' : '.notification-inf-scroll');

      if (scrollEl) {
        setTimeout(() => {
          const childsCounts = scrollEl.childElementCount;
          const childElements = Array.from(scrollEl.querySelectorAll(`.${SCROLL_ITEMS_NAME}`))?.slice(0,MAX_ITEMS_COUNT_FOR_SCROLL);

          if (childsCounts && childsCounts > MAX_ITEMS_COUNT_FOR_SCROLL) {
            setScroll(true);
          } else {
            setScroll(false)
          }

          if (childElements?.length) {
            const height = childElements?.map((el) => el.getBoundingClientRect()?.height);
            const maxPopUpSize = height?.reduce((acc, val) => acc + val, 0) + STICKY_BLOCK_SIZE;
            setPopUpHeight(maxPopUpSize)
          }

        } , 0)
      }
    
    } 
  } , [scrollRef.current, scrollArchiveRef.current, setScroll, setPopUpHeight, open, currentTab, notificationsList.length])

  const closeModal = () => {
    modal.hide();
  }

  useEffect(() => {
    if(notificationsList.length === 1 && isMobile)  updateNotificationStatus()
  }, [isMobile])


  const loader = useMemo(() => {
    return (
      <Placeholder>
        <PlaceholderText>
          {t('loading')}
        </PlaceholderText>
      </Placeholder>
    )
  }, []);

  const onScroll = useCallback(() => {

  }, [currentTab])

  return (
    <>
    {/* @ts-ignore */}
    <NotificationListWrapper width="100%" direction="column" directionMobile="column"  onMouseEnter={() => lockBody()} onMouseLeave={() => unlockBody(false)} onClick={e => e.stopPropagation()} isCGRAVE={isCGRAVE}>
      <ScrollWrapper width="100%" id="notification-dropdown">
        <StickyWrapper isCGRAVE={isCGRAVE}>
          <Header isCGRAVE={isCGRAVE}>
            <BackButton onClick={closeModal}/>
            <Title isCGRAVE={isCGRAVE}>
              {t('notifications.notifications')}
            </Title>
          </Header>
          <TabsWrapper gx={16} isCGRAVE={isCGRAVE}>
            <Tab gx={4}
                 active={currentTab === NotificationTabs.all}
                 isCGRAVE={isCGRAVE}
                 onClick={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                   setCurrentTab(NotificationTabs.all);
                 }}
            >
              <TabText>
                {t('notifications.all')}
              </TabText>
              {unreadCount > 0 && (
                <Circle isGreenAward={isGreenAward} isHSEDesign={isHSEDesign}>
                  <Count count={unreadCount} isBlackText={isGameDeziiign || isJhonata}>
                    {unreadCount}
                  </Count>
                </Circle>
              )}
            </Tab>
            <Tab gx={4}
                 active={currentTab === NotificationTabs.activity}
                 isCGRAVE={isCGRAVE}
                 onClick={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                   setCurrentTab(NotificationTabs.activity);
                 }}
            >
              <TabText>
                {t('notifications.activity')}
              </TabText>
              {categoryUnreadCounts[NotificationBellCategory.activity] > 0 && (
                <Circle isGreenAward={isGreenAward} isHSEDesign={isHSEDesign}>
                  <Count count={categoryUnreadCounts[NotificationBellCategory.activity]} isBlackText={isGameDeziiign || isJhonata}>
                    {categoryUnreadCounts[NotificationBellCategory.activity]}
                  </Count>
                </Circle>
              )}
            </Tab>
            <Tab gx={4}
                 active={currentTab === NotificationTabs.courses}
                 isCGRAVE={isCGRAVE}
                 onClick={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                   setCurrentTab(NotificationTabs.courses);
                 }}
            >
              <TabText>
                {t('notifications.courses_tab')}
              </TabText>
              {categoryUnreadCounts[NotificationBellCategory.courses] > 0 && (
                <Circle isGreenAward={isGreenAward} isHSEDesign={isHSEDesign}>
                  <Count count={categoryUnreadCounts[NotificationBellCategory.courses]} isBlackText={isGameDeziiign || isJhonata}>
                    {categoryUnreadCounts[NotificationBellCategory.courses]}
                  </Count>
                </Circle>
              )}
            </Tab>
            <Tab gx={4}
                 active={currentTab === NotificationTabs.services}
                 isCGRAVE={isCGRAVE}
                 onClick={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                   setCurrentTab(NotificationTabs.services);
                 }}
            >
              <TabText>
                {t('notifications.services_tab')}
              </TabText>
              {categoryUnreadCounts[NotificationBellCategory.services] > 0 && (
                <Circle isGreenAward={isGreenAward} isHSEDesign={isHSEDesign}>
                  <Count count={categoryUnreadCounts[NotificationBellCategory.services]} isBlackText={isGameDeziiign || isJhonata}>
                    {categoryUnreadCounts[NotificationBellCategory.services]}
                  </Count>
                </Circle>
              )}
            </Tab>
            <TabMenuButton
              isCGRAVE={isCGRAVE}
              active={currentTab === NotificationTabs.archived}
            >
              <ArchiveDropdownWrapper>
                <Dropdown
                  offsetY={12}
                  offsetX={0}
                  hideArrow
                  orientation="right"
                  hoverOpen={!isTablet}
                  {...({
                    closeOnMouseLeave: !isTablet,
                  } as any)}
                  target={<MoreButtonTarget size="small" />}
                >
                  <DropdownListItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCurrentTab(NotificationTabs.archived);
                    }}
                  >
                    {t('notifications.archived')}
                  </DropdownListItem>
                </Dropdown>
              </ArchiveDropdownWrapper>
            </TabMenuButton>
          </TabsWrapper>
          {currentTab === NotificationTabs.all && !!notifications?.length && (
            <ButtonsWrapper>
              <Button isCGRAVE={isCGRAVE} onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                updateNotificationStatus();
              }}>
                {t('notifications.mark_as_read')}
              </Button>
              <Button isCGRAVE={isCGRAVE} onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                archiveRead()
              }}>
                {t('notifications.archive_all')}
              </Button>
            </ButtonsWrapper>
          )}
        </StickyWrapper>
        {isInboxTab && scrollContainer && (
          <>
            {notificationsList?.length ? (
              <InfiniteScroll
                ref={scrollRef}
                className={'notification-inf-scroll'}
                dataLength={notificationsList?.length}
                next={getMore}
                hasMore={paging?.notifications?.page < paging?.notifications?.pages}
                loader={loader}
                scrollableTarget={scrollContainer}
                initialScrollY={0}
                scrollThreshold={0.8}
                onScroll={onScroll}
                height={DEFAULT_DROPDOWN_HEIGHT}
              >
                {notificationsList}
              </InfiniteScroll>
            ) : (
              <Placeholder>
                <PlaceholderText>
                  {t('notifications.no_messages')}
                </PlaceholderText>
              </Placeholder>
            )}
          </>
        )}
        {currentTab === NotificationTabs.archived && scrollContainer && (
          <>
            {archivedList?.length ? (
              <InfiniteScroll
                ref={scrollArchiveRef}
                className={'notification-inf-scroll_achirved'}
                dataLength={archivedList?.length}
                next={getMore}
                hasMore={paging?.archived?.page < paging?.archived?.pages}
                loader={loader}
                scrollableTarget={scrollContainer}
                initialScrollY={0}
                scrollThreshold={0.8}
                onScroll={onScroll}
                height={DEFAULT_DROPDOWN_HEIGHT}
              >
                {archivedList}
              </InfiniteScroll>
            ) : (
              <Placeholder>
                <PlaceholderText>
                  {t('notifications.no_messages_archived')}
                </PlaceholderText>
              </Placeholder>
            )}
          </>
        )}
      </ScrollWrapper>
    </NotificationListWrapper>
    </>
  );
}

export default NotificationList;import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from 'react-infinite-scroll-component';

import {
  BackButton,
  Button,
  ButtonsWrapper,
  Header,
  NotificationListWrapper,
  Placeholder,
  PlaceholderText,
  ScrollWrapper,
  StickyWrapper,
  ArchiveDropdownWrapper,
  Tab,
  TabMenuButton,
  TabsWrapper,
  TabText,
  Title,
} from "./styles";
import NotificationListItem from "./NotificationListItem";
import { NotificationTabs, useNotificationContext } from "../../contexts/NotificationContext";
import { useModal } from "../../../core";
import { useTranslation } from "react-i18next";
import { Circle, Count } from "./NotificationBellIcon";
import { DEFAULT_DROPDOWN_HEIGHT, MAX_ITEMS_COUNT_FOR_SCROLL, SCROLL_ITEMS_NAME, STICKY_BLOCK_SIZE } from "./const/const";
import { Dropdown, DropdownListItem, useAdvancedLockBodyScroll, useBreakpoint, useIsMobile } from "@brg/ui";
import { AppTester } from "supporting";
import { MoreButtonTarget } from "shared/components/more-button";
import {
  countUnreadInCategory,
  filterNotificationsByCategory,
  NotificationBellCategory,
} from "./helpers/notificationCategory";

const NotificationList: FC = () => {
  const {
    notifications,
    unreadCount,
    currentTab,
    setCurrentTab,
    archived,
    getMore,
    paging,
    setScroll,
    open,
    updateNotificationStatus,
    archiveRead,
    setPopUpHeight,
  } = useNotificationContext();
  const [lockBody, unlockBody] = useAdvancedLockBodyScroll();
  const modal = useModal();
  const { t, i18n: { language } } = useTranslation();
  const scrollRef = useRef(null);
  const scrollArchiveRef = useRef(null);
  const isMobile = useIsMobile();
  const isCGRAVE = AppTester.isCGRAVE();
  const isHSEDesign = AppTester.isHSEDesign();
  const isGreenAward = AppTester.isGreenAward();
  const isGameDeziiign = AppTester.isGameDeziiign();
  const isJhonata = AppTester.isJhonataNet()
  const isTablet = useBreakpoint(1199);
  const [scrollContainer, setScrollContainer] = useState<any>(null);

  const inboxNotifications = useMemo(() => {
    if (!notifications?.length) {
      return [];
    }
    switch (currentTab) {
      case NotificationTabs.activity:
        return filterNotificationsByCategory(notifications, NotificationBellCategory.activity);
      case NotificationTabs.courses:
        return filterNotificationsByCategory(notifications, NotificationBellCategory.courses);
      case NotificationTabs.services:
        return filterNotificationsByCategory(notifications, NotificationBellCategory.services);
      case NotificationTabs.all:
      default:
        return notifications;
    }
  }, [notifications, currentTab]);

  const categoryUnreadCounts = useMemo(() => ({
    [NotificationBellCategory.activity]: countUnreadInCategory(notifications, NotificationBellCategory.activity),
    [NotificationBellCategory.courses]: countUnreadInCategory(notifications, NotificationBellCategory.courses),
    [NotificationBellCategory.services]: countUnreadInCategory(notifications, NotificationBellCategory.services),
  }), [notifications]);

  const isInboxTab = currentTab !== NotificationTabs.archived;

  const notificationsList = useMemo(() => {
    let rowNots: any = [];

    if (!inboxNotifications?.length) {
      return rowNots;
    }

    inboxNotifications?.forEach((n, idx, arr) => {
      const isLike = n?.actions?.[0]?.type === 'like';

      const key = isLike ? n?.actions?.[0]?.likeInfo?.entityInfo?.entityId : n?.notificationId;
      const name = isLike ? n?.actions?.[0]?.likeInfo?.initiator?.accountId : '';
      const direction = isLike ? n?.actions?.[0]?.likeInfo?.isIncrement ?? true : true;

      const current = isLike
        ? arr[idx]?.actions?.[0]?.likeInfo?.entityInfo?.entityId
        : arr[idx]?.notificationId;      
      const prev = isLike
        ? arr[idx - 1]?.actions?.[0]?.likeInfo?.entityInfo?.entityId
        : arr[idx - 1]?.notificationId;      
      const second = isLike
        ? arr[idx + 1]?.actions?.[0]?.likeInfo?.entityInfo?.entityId
        : arr[idx + 1]?.notificationId;

      const currentDirection = isLike
        ? arr[idx]?.actions?.[0]?.likeInfo?.isIncrement ?? true
        : true
      const prevDirection = isLike
        ? arr[idx - 1]?.actions?.[0]?.likeInfo?.isIncrement ?? true
        : true
      const secondDirection = isLike
        ? arr[idx + 1]?.actions?.[0]?.likeInfo?.isIncrement ?? true
        : true

      if ((current === prev && currentDirection === prevDirection) ||
        (current === second && currentDirection === secondDirection)) {

        const mainRow = rowNots?.find((i: any) => i?.rowId === key && !!i?.mainRow )
        if (!!mainRow) {
          const newValues = rowNots?.map((i: any) => {
            const doubleLike = i?.actions?.[0]?.likeInfo?.initiator?.accountId;
            const doubleLikeDirection = i?.actions?.[0]?.likeInfo?.isIncrement ?? true;
            if (i?.rowId === key && (doubleLike !== name || doubleLikeDirection !== direction)) {
              return {
                ...i,
                rowCount: i?.rowCount + 1
              }
            } return i
          })
          rowNots = newValues;
        }
        else {
          rowNots.push({
            ...n,
            rowId: key,
            mainRow: true,
            rowCount: 0
          })
        }
      } else {
        rowNots.push(n)
      };
    })

    return rowNots?.map((notification: any) => (
      <NotificationListItem key={notification.notificationId} item={notification}/>
    ))
  }, [inboxNotifications, language])

  const archivedList = useMemo(() => {
    return archived?.map((notification) => (
      <NotificationListItem key={notification.notificationId} item={notification}/>
    ))
  }, [archived, language])

  useEffect(() => {
    const scrollContainer = document.querySelector('#notification-dropdown:not(.dropdown-element-popup):last-child');
    setScrollContainer(scrollContainer)
  }, [])

  useEffect(() => {
    if ((scrollRef.current || scrollArchiveRef.current ) && open) {
      const scrollEl = document.querySelector(currentTab === NotificationTabs.archived  ? '.notification-inf-scroll_achirved' : '.notification-inf-scroll');

      if (scrollEl) {
        setTimeout(() => {
          const childsCounts = scrollEl.childElementCount;
          const childElements = Array.from(scrollEl.querySelectorAll(`.${SCROLL_ITEMS_NAME}`))?.slice(0,MAX_ITEMS_COUNT_FOR_SCROLL);

          if (childsCounts && childsCounts > MAX_ITEMS_COUNT_FOR_SCROLL) {
            setScroll(true);
          } else {
            setScroll(false)
          }

          if (childElements?.length) {
            const height = childElements?.map((el) => el.getBoundingClientRect()?.height);
            const maxPopUpSize = height?.reduce((acc, val) => acc + val, 0) + STICKY_BLOCK_SIZE;
            setPopUpHeight(maxPopUpSize)
          }

        } , 0)
      }
    
    } 
  } , [scrollRef.current, scrollArchiveRef.current, setScroll, setPopUpHeight, open, currentTab, notificationsList.length])

  const closeModal = () => {
    modal.hide();
  }

  useEffect(() => {
    if(notificationsList.length === 1 && isMobile)  updateNotificationStatus()
  }, [isMobile])


  const loader = useMemo(() => {
    return (
      <Placeholder>
        <PlaceholderText>
          {t('loading')}
        </PlaceholderText>
      </Placeholder>
    )
  }, []);

  const onScroll = useCallback(() => {

  }, [currentTab])

  return (
    <>
    {/* @ts-ignore */}
    <NotificationListWrapper width="100%" direction="column" directionMobile="column"  onMouseEnter={() => lockBody()} onMouseLeave={() => unlockBody(false)} onClick={e => e.stopPropagation()} isCGRAVE={isCGRAVE}>
      <ScrollWrapper width="100%" id="notification-dropdown">
        <StickyWrapper isCGRAVE={isCGRAVE}>
          <Header isCGRAVE={isCGRAVE}>
            <BackButton onClick={closeModal}/>
            <Title isCGRAVE={isCGRAVE}>
              {t('notifications.notifications')}
            </Title>
          </Header>
          <TabsWrapper gx={16} isCGRAVE={isCGRAVE}>
            <Tab gx={4}
                 active={currentTab === NotificationTabs.all}
                 isCGRAVE={isCGRAVE}
                 onClick={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                   setCurrentTab(NotificationTabs.all);
                 }}
            >
              <TabText>
                {t('notifications.all')}
              </TabText>
              {unreadCount > 0 && (
                <Circle isGreenAward={isGreenAward} isHSEDesign={isHSEDesign}>
                  <Count count={unreadCount} isBlackText={isGameDeziiign || isJhonata}>
                    {unreadCount}
                  </Count>
                </Circle>
              )}
            </Tab>
            <Tab gx={4}
                 active={currentTab === NotificationTabs.activity}
                 isCGRAVE={isCGRAVE}
                 onClick={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                   setCurrentTab(NotificationTabs.activity);
                 }}
            >
              <TabText>
                {t('notifications.activity')}
              </TabText>
              {categoryUnreadCounts[NotificationBellCategory.activity] > 0 && (
                <Circle isGreenAward={isGreenAward} isHSEDesign={isHSEDesign}>
                  <Count count={categoryUnreadCounts[NotificationBellCategory.activity]} isBlackText={isGameDeziiign || isJhonata}>
                    {categoryUnreadCounts[NotificationBellCategory.activity]}
                  </Count>
                </Circle>
              )}
            </Tab>
            <Tab gx={4}
                 active={currentTab === NotificationTabs.courses}
                 isCGRAVE={isCGRAVE}
                 onClick={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                   setCurrentTab(NotificationTabs.courses);
                 }}
            >
              <TabText>
                {t('notifications.courses_tab')}
              </TabText>
              {categoryUnreadCounts[NotificationBellCategory.courses] > 0 && (
                <Circle isGreenAward={isGreenAward} isHSEDesign={isHSEDesign}>
                  <Count count={categoryUnreadCounts[NotificationBellCategory.courses]} isBlackText={isGameDeziiign || isJhonata}>
                    {categoryUnreadCounts[NotificationBellCategory.courses]}
                  </Count>
                </Circle>
              )}
            </Tab>
            <Tab gx={4}
                 active={currentTab === NotificationTabs.services}
                 isCGRAVE={isCGRAVE}
                 onClick={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                   setCurrentTab(NotificationTabs.services);
                 }}
            >
              <TabText>
                {t('notifications.services_tab')}
              </TabText>
              {categoryUnreadCounts[NotificationBellCategory.services] > 0 && (
                <Circle isGreenAward={isGreenAward} isHSEDesign={isHSEDesign}>
                  <Count count={categoryUnreadCounts[NotificationBellCategory.services]} isBlackText={isGameDeziiign || isJhonata}>
                    {categoryUnreadCounts[NotificationBellCategory.services]}
                  </Count>
                </Circle>
              )}
            </Tab>
            <TabMenuButton
              isCGRAVE={isCGRAVE}
              active={currentTab === NotificationTabs.archived}
            >
              <ArchiveDropdownWrapper>
                <Dropdown
                  offsetY={12}
                  offsetX={0}
                  hideArrow
                  orientation="right"
                  hoverOpen={!isTablet}
                  {...({
                    closeOnMouseLeave: !isTablet,
                  } as any)}
                  target={<MoreButtonTarget size="small" />}
                >
                  <DropdownListItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCurrentTab(NotificationTabs.archived);
                    }}
                  >
                    {t('notifications.archived')}
                  </DropdownListItem>
                </Dropdown>
              </ArchiveDropdownWrapper>
            </TabMenuButton>
          </TabsWrapper>
          {currentTab === NotificationTabs.all && !!notifications?.length && (
            <ButtonsWrapper>
              <Button isCGRAVE={isCGRAVE} onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                updateNotificationStatus();
              }}>
                {t('notifications.mark_as_read')}
              </Button>
              <Button isCGRAVE={isCGRAVE} onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                archiveRead()
              }}>
                {t('notifications.archive_all')}
              </Button>
            </ButtonsWrapper>
          )}
        </StickyWrapper>
        {isInboxTab && scrollContainer && (
          <>
            {notificationsList?.length ? (
              <InfiniteScroll
                ref={scrollRef}
                className={'notification-inf-scroll'}
                dataLength={notificationsList?.length}
                next={getMore}
                hasMore={paging?.notifications?.page < paging?.notifications?.pages}
                loader={loader}
                scrollableTarget={scrollContainer}
                initialScrollY={0}
                scrollThreshold={0.8}
                onScroll={onScroll}
                height={DEFAULT_DROPDOWN_HEIGHT}
              >
                {notificationsList}
              </InfiniteScroll>
            ) : (
              <Placeholder>
                <PlaceholderText>
                  {t('notifications.no_messages')}
                </PlaceholderText>
              </Placeholder>
            )}
          </>
        )}
        {currentTab === NotificationTabs.archived && scrollContainer && (
          <>
            {archivedList?.length ? (
              <InfiniteScroll
                ref={scrollArchiveRef}
                className={'notification-inf-scroll_achirved'}
                dataLength={archivedList?.length}
                next={getMore}
                hasMore={paging?.archived?.page < paging?.archived?.pages}
                loader={loader}
                scrollableTarget={scrollContainer}
                initialScrollY={0}
                scrollThreshold={0.8}
                onScroll={onScroll}
                height={DEFAULT_DROPDOWN_HEIGHT}
              >
                {archivedList}
              </InfiniteScroll>
            ) : (
              <Placeholder>
                <PlaceholderText>
                  {t('notifications.no_messages_archived')}
                </PlaceholderText>
              </Placeholder>
            )}
          </>
        )}
      </ScrollWrapper>
    </NotificationListWrapper>
    </>
  );
}

export default NotificationList;